import throttle from 'lodash/throttle'
import React, { useCallback, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  BRIEF_INBOX,
  CUSTOM_CHANNEL,
  INBOX_TYPES,
} from '@disco/constants/GroupTypes'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { toggleShortlists } from '@disco/state-management/actions/AppActions'
import {
  appendTracksToPlaylist,
  loadPane,
  removePlaylistFromGroup,
  togglePlaylistInLibrary,
  updatePane,
} from '@disco/state-management/actions/ExplorerActions'
import {
  resetSelection,
  selectExplorerItems,
} from '@disco/state-management/actions/SelectionActions'
import { deletePlaylist } from '@disco/state-management/actions/ServerActions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import { uploadFilesToPlaylist } from '@disco/state-management/actions/upload-actions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  getAllSelectedItems,
  getExplorerSelection,
  getSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import { getUser } from '@disco/state-management/selectors/user'
import { sendPlaylistDownloadEventThrottled } from '@disco/utils/eventsApi'
import {
  convertGroupStringsToGroupIds,
  getGroupsRestrictions,
  itemRequireAddingToTeamLib,
} from '@disco/utils/groups'
import { handleModifierKeysSelection } from '@disco/utils/selection'

import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'

import { SelectionContext } from 'contexts/selection'

import ActionStar from 'components/common/action-star'
import PlaylistInfo from 'components/common/playlist-info'
import PlaylistActions from 'components/playlist-actions'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'
import type { PaneItemAppearance } from 'components/ui/pane-item'
import PaneItem from 'components/ui/pane-item'
import Tooltip from 'components/ui/tooltip'

import PlaylistDropWrapper from 'containers/PlaylistDropWrapper'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  appearance: PaneItemAppearance
  groups: Group[]
  index: number
  library?: Group
  pane: ExplorerPane
  playlist: Playlist
  showcase?: boolean
  sourceGroupId?: number | null | undefined
  sourceGroupType?: string | null | undefined
  itemPrefix?: string
}

const ExplorerPlaylist = ({
  appearance,
  groups,
  index,
  library,
  pane,
  playlist,
  showcase,
  sourceGroupId,
  sourceGroupType,
  itemPrefix = 'pane',
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const selectionContext = useContext(SelectionContext)
  const explorerSelection = useSelector(getExplorerSelection)
  const selection: Record<string, any> = useSelector((state: State) =>
    getSelectionIndexes(state, selectionContext.id),
  )
  const isPlaylistCreatorOpen = useSelector(state => state.app.shortlistsOpen)
  const isPartOfMultipleSelection: boolean = selection.includes(index)
  const selectedItems = useSelector(getAllSelectedItems)
  const isPhone = useSelector(isDeviceTypePhone)
  const user = useSelector(getUser)
  const isStarred: boolean = useMemo(() => {
    if (!user) return false
    return playlist.groups.includes(user.groups[1].url)
  }, [playlist, user])
  const openPane = useCallback(() => {
    const viewUrl = playlist.versions ? playlist.versions[0] : playlist.url
    let urlParams = ''

    if (library && INBOX_TYPES.includes(library.group_type)) {
      urlParams = viewUrl.includes('?') ? '&share=1' : '?share=1'
    }

    dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${index}` }))
    dispatch(
      loadPane({
        index: pane.index + 1,
        name: playlist.name,
        title: playlist.name,
        // pane.title is deprecated
        type: 'playlist',
        url: viewUrl + urlParams,
        meta: {
          briefId: pane.meta?.briefId,
          inboxId: sourceGroupId,
          submission: {
            version: playlist.brief_version_submitted_to,
            versionCreated: playlist.version_created_at,
            isLatestVersion:
              playlist.latest_version === playlist.brief_version_submitted_to,
          },
        },
      }),
    )
    if (library.group_type === BRIEF_INBOX && pane.meta?.briefId) {
      const targetUrl = pane.meta?.briefProjectId
        ? `/briefs/projects/${pane.meta?.briefProjectId}/${pane.meta?.briefId}/submissions/${playlist.playlist_id}`
        : `/briefs/${pane.meta?.briefId}/submissions/${playlist.playlist_id}`
      const currentUrl = window.location.pathname
      if (currentUrl !== targetUrl) {
        window.history.pushState({}, '', targetUrl)
      }
    }
  }, [dispatch, index, library, pane, playlist])

  const handleClick = useCallback(
    (event: MouseEvent) => {
      // handle selection actions
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        handleModifierKeysSelection(event, {
          selection: explorerSelection,
          index,
          selectionId: selectionContext.id,
          pane,
          selectItems: (_pane, selectionRange, selectionType) =>
            dispatch(
              selectExplorerItems(
                selectionContext.id,
                selectionRange,
                selectionType,
              ),
            ),
        })
        return
      }

      // handle regular clicks, regular click reset selection
      dispatch(resetSelection())
      openPane()
    },
    [dispatch, explorerSelection, index, openPane, pane, selectionContext.id],
  )
  const handleFavoriteClick = useCallback(() => {
    if (isStarred && playlist.groups.length === 1) {
      return navigate(
        `/explorer/playlists/${playlist.playlist_id}/remove-from-library`,
      )
    }

    dispatch(togglePlaylistInLibrary(playlist))

    if (library && INBOX_TYPES.includes(library.group_type) && !isStarred) {
      dispatch(sendMixpanelEvent('Inbox import playlist'))
      sendPlaylistDownloadEventThrottled(
        playlist.playlist_id,
        undefined,
        pane.groupId,
      )
    }
  }, [dispatch, isStarred, library, navigate, pane, playlist])
  const handleDelete = useCallback(() => {
    if (user.settings && user.settings.hide_inbox_removal_warning) {
      dispatch(deletePlaylist(playlist))
      dispatch(sendMixpanelEvent('Inbox remove playlist'))
    } else if (library) {
      navigate(
        `/inboxes/${library.id}/playlists/${playlist.playlist_id}/remove`,
      )
    }
  }, [dispatch, library, navigate, playlist, user])
  const handleGroupRemoveClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()
      const targetGroup: Group | null | undefined = groups.find(
        (group: Group) => group.id === pane.groupId,
      )

      if (targetGroup && playlist) {
        dispatch(removePlaylistFromGroup(playlist, targetGroup))
      }

      if (library && INBOX_TYPES.includes(library.group_type) && !isStarred) {
        dispatch(sendMixpanelEvent('Inbox remove playlist'))
      }
    },
    [dispatch, groups, isStarred, library, pane, playlist],
  )
  const openPlaylist = throttle(() => {
    dispatch(createNewShortlist(playlist))
    dispatch(sendMixpanelEvent('Edit playlist'))
    if (!isPlaylistCreatorOpen) {
      const isSearchPage = Boolean(window.location.pathname.includes('/search'))
      const currentLocation = isSearchPage ? 'Search' : 'Explorer'
      dispatch(toggleShortlists(true, currentLocation, 'Edit Playlist'))
    }

    if (library && INBOX_TYPES.includes(library.group_type)) {
      sendPlaylistDownloadEventThrottled(
        playlist.playlist_id,
        undefined,
        pane.groupId,
      )
    }
  }, 1000)
  const handleEditClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()
      openPlaylist()
    },
    [openPlaylist],
  )
  const handleDragStart = useCallback(() => {
    dispatch(resetSelection())
  }, [dispatch])

  const renderActions = () => {
    let removeButton = null

    if (pane.groupId && library) {
      const restricted = getGroupsRestrictions(groups, [
        library.id,
      ])?.lock_content
      const deleteRestricted = getGroupsRestrictions(
        groups,
        convertGroupStringsToGroupIds(playlist.groups),
      )?.lock_content

      if (library.group_type === CUSTOM_CHANNEL && !restricted) {
        removeButton = (
          <Tooltip content="Remove from channel">
            <Button
              appearance="icon"
              compact
              icon="clear"
              onClick={handleGroupRemoveClick}
            />
          </Tooltip>
        )
      } else if (INBOX_TYPES.includes(library.group_type) && !restricted) {
        if (!itemRequireAddingToTeamLib(playlist.groups, groups)) {
          removeButton = (
            <Tooltip content="Remove from inbox">
              <Button
                appearance="icon"
                compact
                icon="clear"
                onClick={handleGroupRemoveClick}
              />
            </Tooltip>
          )
        } else if (!deleteRestricted)
          removeButton = (
            <Tooltip content="Remove from DISCO">
              <Button
                appearance="icon"
                compact
                icon="clear"
                onClick={handleDelete}
              />
            </Tooltip>
          )
      }
    }

    return (
      <ButtonContainer>
        {playlist?.permissions?.manage && !isPhone && (
          <>
            <Tooltip content="Edit playlist">
              <Button
                appearance="icon"
                compact
                icon="edit"
                onClick={handleEditClick}
              />
            </Tooltip>
            <ActionStar active={isStarred} onClick={handleFavoriteClick} />
            {removeButton}
          </>
        )}

        <PlaylistActions
          buttonSize="compact"
          playlist={playlist}
          mixpanelContext="Explorer"
          hideShare={!playlist?.permissions?.share_link}
        />
      </ButtonContainer>
    )
  }

  return (
    <PlaylistDropWrapper
      appendTracksToPlaylist={(playlist, tracks) => {
        dispatch(appendTracksToPlaylist(playlist, tracks))
      }}
      uploadFilesToPlaylist={(files, playlist) => {
        dispatch(uploadFilesToPlaylist(files, playlist))
      }}
      openPane={openPane}
      playlist={playlist}
      selectedItems={selectedItems}
    >
      <Draggable
        id={`${pane.index}-playlist-${playlist.playlist_id}`}
        data={{
          isPartOfMultipleSelection,
          playlist,
          selection,
          sourceGroupId,
          sourceGroupType,
        }}
        type={PLAYLIST}
        onDragStart={isPartOfMultipleSelection ? undefined : handleDragStart}
      >
        <HoverProvider enableTouchSupport>
          <PaneItem
            appearance={appearance}
            content={
              <PlaylistInfo
                playlist={playlist}
                showcase={showcase}
                libraryGroupType={library && library.group_type}
              />
            }
            actions={renderActions()}
            onClick={handleClick}
          />
        </HoverProvider>
      </Draggable>
    </PlaylistDropWrapper>
  )
}

export default ExplorerPlaylist
